import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { AppHeader } from '@/components/AppHeader';
import { LeagueSettings } from '@/components/LeagueSettings';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { 
  Users, 
  Calendar, 
  Trophy, 
  Lock, 
  Globe,
  MoreHorizontal,
  Share,
  Copy,
  Eye,
  EyeOff,
  LogOut,
  Settings,
  Clock,
  TrendingUp,
  Activity,
  Shield,
  Trash2,
  AlertTriangle,
  Save,
  RefreshCw,
  Shuffle,
  Search,
  Filter,
  Plus,
  CheckCircle,
  XCircle,
  Volume2,
  VolumeX,
  Wifi,
  Download,
  Star,
  PlayCircle,
  PauseCircle,
  StopCircle
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface League {
  id: string;
  name: string;
  description?: string;
  owner_id: string;
  is_private: boolean;
  password_hash?: string;
  draft_at?: string;
  starters_count: number;
  bench_count: number;
  max_teams?: number;
  created_at: string;
  updated_at: string;
  status?: string;
}

interface LeagueParticipant {
  id: string;
  user_id: string;
  league_id: string;
  created_at: string;
  updated_at: string;
  profiles: {
    username: string;
    display_name?: string;
  };
}

interface ActivityEvent {
  id: string;
  type: 'join' | 'draft' | 'trade' | 'score' | 'ready' | 'order';
  message: string;
  created_at: string;
  user_name?: string;
}

interface DraftOrder {
  round: number;
  pick: number;
  team_id: string;
  team_name: string;
  owner_name: string;
  is_current_user: boolean;
}

interface DraftReadiness {
  id: string;
  league_id: string;
  user_id: string;
  is_ready: boolean;
  updated_at: string;
}

interface ParticipantReadiness {
  user_id: string;
  team_name: string;
  owner_name: string;
  is_ready: boolean;
  is_online: boolean;
  avatar_url?: string;
}

interface QueuedPlayer {
  id: string;
  name: string;
  position: 'GK' | 'DEF' | 'MID' | 'FWD';
  team: string;
  photo_url?: string;
  stats?: {
    goals: number;
    assists: number;
    points: number;
  };
}

interface SystemCheck {
  connection: boolean;
  notifications: boolean;
  audio: boolean;
}

const LeagueOverview = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [league, setLeague] = useState<League | null>(null);
  const [participants, setParticipants] = useState<LeagueParticipant[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [isParticipant, setIsParticipant] = useState(false);
  const [loading, setLoading] = useState(true);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [draftCountdown, setDraftCountdown] = useState<string>('');
  
  // Draft state
  const [draftOrder, setDraftOrder] = useState<DraftOrder[]>([]);
  const [participantReadiness, setParticipantReadiness] = useState<ParticipantReadiness[]>([]);
  const [myQueue, setMyQueue] = useState<QueuedPlayer[]>([]);
  const [availablePlayers] = useState<QueuedPlayer[]>([
    {
      id: '1',
      name: 'Erling Haaland',
      position: 'FWD',
      team: 'Man City',
      stats: { goals: 36, assists: 8, points: 272 }
    },
    {
      id: '2', 
      name: 'Mohamed Salah',
      position: 'FWD',
      team: 'Liverpool',
      stats: { goals: 24, assists: 12, points: 228 }
    },
    {
      id: '3',
      name: 'Kevin De Bruyne', 
      position: 'MID',
      team: 'Man City',
      stats: { goals: 7, assists: 16, points: 180 }
    }
  ]);
  const [playerSearchTerm, setPlayerSearchTerm] = useState('');
  const [playerPositionFilter, setPlayerPositionFilter] = useState('all');
  const [systemCheck, setSystemCheck] = useState<SystemCheck>({
    connection: true,
    notifications: true,
    audio: false
  });
  const [shuffleModalOpen, setShuffleModalOpen] = useState(false);
  const [isUserReady, setIsUserReady] = useState(false);
  const [draftReadiness, setDraftReadiness] = useState<DraftReadiness[]>([]);
  const [isUpdatingReadiness, setIsUpdatingReadiness] = useState(false);

  // Mock data for demo
  const mockActivity: ActivityEvent[] = [
    {
      id: '1',
      type: 'join',
      message: 'Alex Johnson joined the league',
      created_at: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
      user_name: 'Alex Johnson'
    },
    {
      id: '2',
      type: 'order',
      message: 'Draft order has been generated',
      created_at: new Date(Date.now() - 45 * 60 * 1000).toISOString()
    },
    {
      id: '3',
      type: 'ready',
      message: 'Sarah Chen marked as ready',
      created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      user_name: 'Sarah Chen'
    }
  ];

  const keyDates = [
    {
      label: 'Draft Start',
      date: '2024-09-15 20:00',
      icon: Calendar
    },
    {
      label: 'Trade Deadline',
      date: '2024-10-01 23:59',
      icon: TrendingUp
    },
    {
      label: 'Next Lock Time',
      date: '2024-09-20 18:00',
      icon: Clock
    }
  ];

  useEffect(() => {
    if (id) {
      fetchLeagueDetails();
    }
  }, [id]);

  useEffect(() => {
    // Update countdown every second for draft time
    const interval = setInterval(() => {
      if (league?.draft_at) {
        const now = new Date().getTime();
        const draftTime = new Date(league.draft_at).getTime();
        const difference = draftTime - now;

        if (difference > 0) {
          const days = Math.floor(difference / (1000 * 60 * 60 * 24));
          const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((difference % (1000 * 60)) / 1000);

          if (days > 0) {
            setDraftCountdown(`${days}d ${hours}h ${minutes}m`);
          } else {
            setDraftCountdown(`${hours}h ${minutes}m ${seconds}s`);
          }
        } else {
          setDraftCountdown(league?.status === 'live' ? 'Draft started!' : 'Awaiting start');
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [league?.draft_at, league?.status]);

  // Fetch and subscribe to draft readiness
  useEffect(() => {
    if (!league?.id || !user?.id) return;

    const fetchReadiness = async () => {
      const { data, error } = await supabase
        .from('draft_readiness')
        .select('*')
        .eq('league_id', league.id);
      
      if (error) {
        console.error('Error fetching readiness:', error);
        return;
      }
      
      setDraftReadiness(data || []);
    };

    fetchReadiness();

    // Subscribe to real-time changes for draft readiness
    const readinessChannel = supabase
      .channel(`draft_readiness_${league.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'draft_readiness',
          filter: `league_id=eq.${league.id}`
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setDraftReadiness(prev => [...prev, payload.new as DraftReadiness]);
          } else if (payload.eventType === 'UPDATE') {
            setDraftReadiness(prev => 
              prev.map(r => r.id === payload.new.id ? payload.new as DraftReadiness : r)
            );
          } else if (payload.eventType === 'DELETE') {
            setDraftReadiness(prev => prev.filter(r => r.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    // Subscribe to league participants changes for real-time updates
    const participantsChannel = supabase
      .channel(`participants_${league.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'league_players',
          filter: `league_id=eq.${league.id}`
        },
        (payload) => {
          console.log('Participant change:', payload);
          // Refresh participants when someone joins/leaves
          fetchLeagueDetails();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'league_teams',
          filter: `league_id=eq.${league.id}`
        },
        (payload) => {
          console.log('Team change:', payload);
          // Refresh participants when bot teams are added
          fetchLeagueDetails();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(readinessChannel);
      supabase.removeChannel(participantsChannel);
    };
  }, [league?.id, user?.id]);

  // Update participant readiness based on draft_readiness data
  useEffect(() => {
    if (!participants.length) return;

    const updatedReadiness = participants.map(participant => {
      const readinessRecord = draftReadiness.find(r => r.user_id === participant.user_id);
      return {
        user_id: participant.user_id,
        team_name: `Team ${participant.profiles?.display_name || participant.profiles?.username}`,
        owner_name: participant.profiles?.display_name || participant.profiles?.username || 'Unknown',
        avatar_url: undefined, // TODO: Add avatar support
        is_ready: readinessRecord?.is_ready || false,
        is_online: true // TODO: Add presence tracking
      };
    });

    setParticipantReadiness(updatedReadiness);
  }, [participants, draftReadiness]);

  const fetchLeagueDetails = async () => {
    if (!user || !id) return;

    try {
      setLoading(true);

      // Fetch league details
      const { data: leagueData, error: leagueError } = await supabase
        .from('leagues')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (leagueError) throw leagueError;
      if (!leagueData) {
        navigate('/leagues');
        return;
      }

      setLeague(leagueData);
      setIsOwner(leagueData.owner_id === user.id);

      // Fetch participants with user details
      const { data: playersData, error: playersError } = await supabase
        .from('league_players')
        .select('*')
        .eq('league_id', id)
        .order('created_at', { ascending: true });

      if (playersError) throw playersError;

      // Fetch bot teams to show them as participants
      const { data: botTeams } = await supabase
        .from('league_teams')
        .select('*')
        .eq('league_id', id)
        .eq('is_bot', true)
        .order('created_at', { ascending: true });

      // Get profiles for each participant
      const participantsWithProfiles = await Promise.all(
        (playersData || []).map(async (participant) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('username, display_name')
            .eq('user_id', participant.user_id)
            .maybeSingle();

          return {
            ...participant,
            profiles: profile || { username: 'Unknown', display_name: null }
          };
        })
      );

      // Map bot teams into participant-like entries
      const botParticipants: LeagueParticipant[] = (botTeams || []).map((team: any) => ({
        id: `bot-${team.id}`,
        user_id: `bot-${team.id}`,
        league_id: team.league_id,
        created_at: team.created_at,
        updated_at: team.updated_at,
        profiles: {
          username: 'bot',
          display_name: team.team_name
        }
      }));

      const combined = [...(participantsWithProfiles || []), ...botParticipants];

      setParticipants(combined);
      setIsParticipant(playersData?.some(p => p.user_id === user.id) || false);

    } catch (error) {
      console.error('Error fetching league details:', error);
      toast({
        title: 'Error loading league',
        description: 'Please try refreshing the page.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: `${label} copied!`,
      description: 'The link has been copied to your clipboard.',
    });
  };

  const toggleReadiness = async (isReady: boolean) => {
    if (!user?.id || !league?.id || isUpdatingReadiness) return;

    setIsUpdatingReadiness(true);
    
    try {
      const { error } = await supabase
        .from('draft_readiness')
        .upsert({
          league_id: league.id,
          user_id: user.id,
          is_ready: isReady
        }, { onConflict: 'league_id,user_id' });

      if (error) throw error;

      toast({
        title: isReady ? 'Marked as ready!' : 'Marked as not ready',
        description: 'Other participants will see this update in real-time',
      });
    } catch (error) {
      console.error('Error updating readiness:', error);
      toast({
        title: 'Error updating readiness',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingReadiness(false);
    }
  };

  const startDraftNow = async () => {
    if (!league?.id || !user?.id) return;

    try {
      // Broadcast draft start signal
      const channel = supabase.channel(`draft_start_${league.id}`);
      await channel.send({
        type: 'broadcast',
        event: 'draft:start',
        payload: { league_id: league.id, started_by: user.id }
      });

      toast({
        title: 'Draft started!',
        description: 'Redirecting to draft room...',
      });
      
      setTimeout(() => {
        navigate(`/draft-room/${league.id}`);
      }, 1000);
    } catch (error) {
      console.error('Error starting draft:', error);
      toast({
        title: 'Failed to start draft',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const leaveLeague = async () => {
    if (!user || !league) return;

    try {
      const { error } = await supabase
        .from('league_participants')
        .delete()
        .eq('league_id', league.id)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: 'Left league successfully',
        description: 'You have been removed from the league.',
      });

      navigate('/leagues');
    } catch (error) {
      console.error('Error leaving league:', error);
      toast({
        title: 'Error leaving league',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMinutes = Math.floor((now.getTime() - time.getTime()) / (1000 * 60));

    if (diffMinutes < 60) return `${diffMinutes}m`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h`;
    return `${Math.floor(diffMinutes / 1440)}d`;
  };

  const getDraftStatus = () => {
    if (!league) return { text: 'Loading...', variant: 'secondary' };
    
    const now = new Date();
    const draftTime = new Date(league.draft_at);
    
    if (now < draftTime) {
      return { text: 'Upcoming', variant: 'secondary' };
    } else {
      return { text: 'Active', variant: 'default' };
    }
  };

  const renderDraftStatus = () => {
    const status = getDraftStatus();
    const now = new Date();
    const draftTime = new Date(league?.draft_at || '');
    const allReady = participantReadiness.length > 0 && participantReadiness.every(p => p.is_ready);
    const canStartDraft = isOwner && allReady && now < draftTime;
    
    if (now < draftTime) {
      return (
        <div className="space-y-2">
          <Badge variant={status.variant as any}>{status.text}</Badge>
          <div className="text-sm text-muted-foreground">
            <div>Draft: {draftTime.toLocaleDateString()} at {draftTime.toLocaleTimeString()}</div>
            <div className="font-mono text-primary font-semibold">{draftCountdown}</div>
          </div>
          {canStartDraft && (
            <Button 
              size="sm" 
              variant="default"
              onClick={startDraftNow}
              className="bg-green-600 hover:bg-green-700"
            >
              Start Draft Now
            </Button>
          )}
        </div>
      );
    } else {
      return (
        <div className="space-y-2">
          <Badge variant={status.variant as any}>{status.text}</Badge>
          <div className="text-sm text-muted-foreground">Draft in progress</div>
          <Button 
            size="sm" 
            variant="default"
            onClick={() => navigate(`/draft-room/${league?.id}`)}
          >
            Go to Draft Room
          </Button>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <AppHeader />
          <div className="flex-1 overflow-auto">
            <div className="container mx-auto px-4 py-6">
              <div className="text-center">
                <Trophy className="h-12 w-12 mx-auto text-primary animate-pulse mb-4" />
                <p className="text-muted-foreground">Loading league details...</p>
              </div>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  if (!league) {
    return (
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <AppHeader />
          <div className="flex-1 overflow-auto">
            <div className="container mx-auto px-4 py-6">
              <div className="text-center">
                <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h2 className="text-2xl font-bold mb-2">League not found</h2>
                <p className="text-muted-foreground mb-4">This league may have been deleted or made private.</p>
                <Button onClick={() => navigate('/leagues')}>
                  Back to Leagues
                </Button>
              </div>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  const shareUrl = `${window.location.origin}/league/${league.id}`;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <AppHeader />
        <div className="flex-1 overflow-auto">
          <div className="container mx-auto px-4 py-6 space-y-6">
            {/* League Header Card */}
            <Card className="bg-gradient-subtle border-primary/10">
              <CardHeader>
                <div className="flex items-start justify-between">
                  {/* Left: League Info */}
                  <div className="flex-1 space-y-3">
                    <div>
                      <h1 className="text-3xl font-bold">{league.name}</h1>
                      {league.description && (
                        <p className="text-muted-foreground mt-1 text-base">{league.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-muted-foreground">
                          Owner: <span className="font-medium">@{participants.find(p => p.user_id === league.owner_id)?.profiles?.username || 'Unknown'}</span>
                        </span>
                        <Badge variant="outline" className="gap-1">
                          <Globe className="h-3 w-3" />
                          {league.is_private ? 'Private' : 'Public'}
                        </Badge>
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {participants.length}/{league.max_teams || 12} players
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Center: Draft Status */}
                  <div className="flex-1 flex justify-center">
                    {renderDraftStatus()}
                  </div>

                  {/* Right: Action Buttons */}
                  <div className="flex items-center gap-2">
                    {isParticipant && (
                      <Button 
                        onClick={() => navigate(`/league/${league.id}/player/${participants.find(p => p.user_id === user?.id)?.id}/gw/1`)}
                        className="gap-2 bg-primary hover:bg-primary/90"
                      >
                        <Users className="h-4 w-4" />
                        Set Lineup
                      </Button>
                    )}
                    
                    <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="gap-2">
                          <Share className="h-4 w-4" />
                          Invite Members
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Invite to {league.name}</DialogTitle>
                          <DialogDescription>
                            Share this link with others to invite them to the league
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Share Link</label>
                            <div className="flex gap-2">
                              <div className="flex-1 p-3 bg-muted rounded-md font-mono text-sm break-all">
                                {shareUrl}
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => copyToClipboard(shareUrl, 'Link')}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Anyone with the link can request to join this public league.
                          </p>
                        </div>
                      </DialogContent>
                    </Dialog>

                    {!isOwner && isParticipant && (
                      <Button variant="outline" onClick={leaveLeague} className="gap-2 text-destructive hover:text-destructive">
                        <LogOut className="h-4 w-4" />
                        Leave League
                      </Button>
                    )}

                    {isOwner && (
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/league/${league.id}/settings`)}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Sticky Tabs */}
            <div className="sticky top-16 z-10 bg-background/95 backdrop-blur-sm border-b">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-5 bg-muted/50">
                  <TabsTrigger value="overview" className="data-[state=active]:border-b-2 data-[state=active]:border-primary">Overview</TabsTrigger>
                  <TabsTrigger value="draft" className="data-[state=active]:border-b-2 data-[state=active]:border-primary">Draft</TabsTrigger>
                  <TabsTrigger value="players" className="data-[state=active]:border-b-2 data-[state=active]:border-primary">Players</TabsTrigger>
                  <TabsTrigger value="matchups" className="data-[state=active]:border-b-2 data-[state=active]:border-primary">Matchups</TabsTrigger>
                  <TabsTrigger value="settings" className="data-[state=active]:border-b-2 data-[state=active]:border-primary">Settings</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Tab Content */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              
              {/* Matchups Tab Content */}
              <TabsContent value="matchups" className="mt-0">
                <div className="space-y-6">
                  {/* Filters */}
                  <div className="flex items-center gap-4">
                    <Select defaultValue="current">
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Select Gameweek" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="current">Current Week (GW 5)</SelectItem>
                        <SelectItem value="4">Gameweek 4</SelectItem>
                        <SelectItem value="3">Gameweek 3</SelectItem>
                        <SelectItem value="6">Gameweek 6</SelectItem>
                        <SelectItem value="7">Gameweek 7</SelectItem>
                      </SelectContent>
                    </Select>

                    <Tabs defaultValue="current">
                      <TabsList>
                        <TabsTrigger value="current">Current Week</TabsTrigger>
                        <TabsTrigger value="past">Past Weeks</TabsTrigger>
                        <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
                      </TabsList>
                    </Tabs>

                    <Button variant="outline" className="ml-auto">
                      <Calendar className="h-4 w-4 mr-2" />
                      Schedule View
                    </Button>
                  </div>

                  {/* Main Matchups Content */}
                  <div className="grid grid-cols-12 gap-6">
                    {/* Main Matchups Grid */}
                    <div className="col-span-8 space-y-4">
                      {/* Sample Matchup Cards */}
                      <Card className="cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-1 ring-2 ring-green-500 ring-offset-2">
                        <CardContent className="p-6">
                          <div className="grid grid-cols-3 items-center gap-4">
                            {/* Team A */}
                            <div className="text-center">
                              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full mx-auto mb-2 flex items-center justify-center text-white font-bold text-lg">
                                LB
                              </div>
                              <h3 className="font-semibold">Lightning Bolts</h3>
                              <p className="text-sm text-muted-foreground">{participants[0]?.profiles?.display_name || participants[0]?.profiles?.username || 'Player 1'}</p>
                              <div className="text-2xl font-bold text-blue-600 mt-2">87</div>
                              <p className="text-xs text-muted-foreground">points</p>
                            </div>

                            {/* VS Section */}
                            <div className="text-center space-y-3">
                              <div className="text-lg font-bold text-muted-foreground">VS</div>
                              <Badge variant="destructive" className="mx-auto flex items-center gap-1 w-fit">
                                <Activity className="h-3 w-3" />
                                LIVE
                              </Badge>
                              <div className="space-y-1">
                                <Progress value={65} className="h-2 bg-red-100" />
                                <div className="flex justify-between text-xs text-muted-foreground">
                                  <span>65%</span>
                                  <span>35%</span>
                                </div>
                              </div>
                              <Button size="sm" variant="outline">
                                <Eye className="h-3 w-3 mr-1" />
                                View Details
                              </Button>
                            </div>

                            {/* Team B */}
                            <div className="text-center">
                              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-full mx-auto mb-2 flex items-center justify-center text-white font-bold text-lg">
                                TC
                              </div>
                              <h3 className="font-semibold">Thunder Cats</h3>
                              <p className="text-sm text-muted-foreground">{participants[1]?.profiles?.display_name || participants[1]?.profiles?.username || 'Player 2'}</p>
                              <div className="text-2xl font-bold text-red-600 mt-2">92</div>
                              <p className="text-xs text-muted-foreground">points</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-1">
                        <CardContent className="p-6">
                          <div className="grid grid-cols-3 items-center gap-4">
                            {/* Team A */}
                            <div className="text-center">
                              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full mx-auto mb-2 flex items-center justify-center text-white font-bold text-lg">
                                SR
                              </div>
                              <h3 className="font-semibold">Storm Riders</h3>
                              <p className="text-sm text-muted-foreground">{participants[2]?.profiles?.display_name || participants[2]?.profiles?.username || 'Player 3'}</p>
                              <div className="text-2xl font-bold text-purple-600 mt-2">78</div>
                              <p className="text-xs text-muted-foreground">points</p>
                            </div>

                            {/* VS Section */}
                            <div className="text-center space-y-3">
                              <div className="text-lg font-bold text-muted-foreground">VS</div>
                              <Badge variant="default" className="mx-auto flex items-center gap-1 w-fit">
                                <CheckCircle className="h-3 w-3" />
                                FINAL
                              </Badge>
                              <div className="space-y-1">
                                <Progress value={72} className="h-2 bg-red-100" />
                                <div className="flex justify-between text-xs text-muted-foreground">
                                  <span>72%</span>
                                  <span>28%</span>
                                </div>
                              </div>
                              <Button size="sm" variant="outline">
                                <Eye className="h-3 w-3 mr-1" />
                                View Details
                              </Button>
                            </div>

                            {/* Team B */}
                            <div className="text-center">
                              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full mx-auto mb-2 flex items-center justify-center text-white font-bold text-lg">
                                FH
                              </div>
                              <h3 className="font-semibold">Fire Hawks</h3>
                              <p className="text-sm text-muted-foreground">{participants[3]?.profiles?.display_name || participants[3]?.profiles?.username || 'Player 4'}</p>
                              <div className="text-2xl font-bold text-orange-600 mt-2">71</div>
                              <p className="text-xs text-muted-foreground">points</p>
                            </div>
                          </div>

                          <div className="mt-4 text-center">
                            <Trophy className="h-4 w-4 inline mr-1 text-yellow-500" />
                            <span className="text-sm font-medium">Storm Riders wins!</span>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Sidebar */}
                    <div className="col-span-4 space-y-6">
                      {/* League Scoreboard */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Trophy className="h-5 w-5" />
                            League Scoreboard
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center p-2 rounded-lg text-sm bg-green-50 dark:bg-green-950/30">
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">Lightning Bolts</span>
                                  <span className="text-muted-foreground">vs</span>
                                  <span className="font-medium">Thunder Cats</span>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-mono font-bold">87 - 92</div>
                                <Badge variant="outline">live</Badge>
                              </div>
                            </div>
                            <div className="flex justify-between items-center p-2 rounded-lg text-sm">
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">Storm Riders</span>
                                  <span className="text-muted-foreground">vs</span>
                                  <span className="font-medium">Fire Hawks</span>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-mono font-bold">78 - 71</div>
                                <Badge variant="outline">final</Badge>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* News & Updates */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Activity className="h-5 w-5" />
                            News & Updates
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div className="text-sm p-2 bg-muted/30 rounded-lg">
                              Haaland scores hat-trick in City's 4-1 victory
                            </div>
                            <div className="text-sm p-2 bg-muted/30 rounded-lg">
                              Injury update: Salah substituted with hamstring concern
                            </div>
                            <div className="text-sm p-2 bg-muted/30 rounded-lg">
                              Liverpool clean sheet streak continues to 5 games
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="overview" className="mt-0">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column - Standings */}
                  <div className="lg:col-span-2 space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Trophy className="h-5 w-5" />
                          Standings
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {participants.length === 0 ? (
                          <div className="text-center py-8">
                            <Trophy className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                            <h3 className="text-lg font-semibold mb-2">Standings will appear after the draft</h3>
                            <p className="text-muted-foreground mb-4">
                              {isOwner ? "Set up your draft to get started" : "Wait for the league owner to set up the draft"}
                            </p>
                            {isOwner && (
                              <Button>
                                Go to Draft Room
                              </Button>
                            )}
                          </div>
                        ) : (
                          <>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-16">Rank</TableHead>
                                  <TableHead>Team Name</TableHead>
                                  <TableHead>Owner</TableHead>
                                  <TableHead className="text-center">W-L</TableHead>
                                  <TableHead className="text-right font-mono">Points</TableHead>
                                  <TableHead className="text-center">Streak</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {participants.map((participant, index) => (
                                  <TableRow 
                                    key={participant.id}
                                    className={participant.user_id === user?.id ? "bg-primary/5 border-primary/20" : ""}
                                  >
                                    <TableCell className="font-medium">
                                      {index + 1}
                                    </TableCell>
                                    <TableCell className="font-medium">
                                      Team {participant.profiles?.display_name || participant.profiles?.username}
                                    </TableCell>
                                    <TableCell>
                                      @{participant.profiles?.username}
                                      {participant.user_id === user?.id && <Badge variant="secondary" className="ml-2">You</Badge>}
                                    </TableCell>
                                    <TableCell className="text-center font-mono">0-0</TableCell>
                                    <TableCell className="text-right font-mono">0</TableCell>
                                    <TableCell className="text-center">-</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            <div className="mt-4">
                              <Button 
                                variant="link" 
                                className="p-0 h-auto text-sm"
                                onClick={() => setActiveTab('players')}
                              >
                                View Full Standings
                              </Button>
                            </div>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Right Column - Widgets */}
                  <div className="space-y-6">
                    {/* Recent Activity */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Activity className="h-5 w-5" />
                          Recent Activity
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {mockActivity.length === 0 ? (
                          <div className="text-center py-6">
                            <p className="text-muted-foreground text-sm">
                              No activity yet. Invite members to get started.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {mockActivity.map((activity) => (
                              <div key={activity.id} className="flex items-start gap-3 pb-3 border-b border-border/50 last:border-0">
                                <div className="flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-2" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm">{activity.message}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {formatTimeAgo(activity.created_at)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Key Dates */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Calendar className="h-5 w-5" />
                          Key Dates
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {keyDates.map((date, index) => (
                            <div key={index} className="flex items-center gap-3">
                              <date.icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-sm font-medium">{date.label}</p>
                                <p className="text-xs text-muted-foreground">{date.date}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="draft" className="mt-0">
                {/* Draft Status Header */}
                <Card className="mb-6">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      {/* Left: Status and Countdown */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <Badge 
                            variant={getDraftStatus().text === 'Live' ? 'destructive' : getDraftStatus().text === 'Completed' ? 'secondary' : 'default'}
                            className="text-sm"
                          >
                            {getDraftStatus().text === 'Active' ? 'Live' : getDraftStatus().text}
                          </Badge>
                          {league?.draft_at && (
                            <div className="text-sm text-muted-foreground">
                              {new Date(league.draft_at).toLocaleDateString()} at {new Date(league.draft_at).toLocaleTimeString()} EST
                            </div>
                          )}
                        </div>
                        
                        {getDraftStatus().text === 'Upcoming' && draftCountdown && (
                          <div className="font-mono text-2xl font-bold text-primary">
                            {draftCountdown}
                          </div>
                        )}
                        
                        {getDraftStatus().text === 'Live' && (
                          <div className="space-y-2">
                            <div className="text-lg font-semibold">Team Arsenal FC is on the clock</div>
                            <div className="flex items-center gap-3">
                              <div className="font-mono text-xl font-bold text-destructive">00:45</div>
                              <Progress value={75} className="w-32 h-2" />
                            </div>
                          </div>
                        )}
                      </div>
                      
                       {/* Right: Action Buttons */}
                       <div className="flex items-center gap-3">
                         {isOwner && getDraftStatus().text === 'Upcoming' && (
                           <Button variant="outline" size="sm" onClick={() => setActiveTab('settings')}>
                             <Settings className="h-4 w-4 mr-2" />
                             Edit Draft Settings
                           </Button>
                         )}
                         
                         {getDraftStatus().text === 'Upcoming' && participantReadiness.length > 0 && participantReadiness.every(p => p.is_ready) && isOwner && (
                           <Button 
                             className="bg-green-600 hover:bg-green-700"
                             onClick={startDraftNow}
                           >
                             <PlayCircle className="h-4 w-4 mr-2" />
                             Start Draft Now
                           </Button>
                         )}
                         
                         {getDraftStatus().text === 'Upcoming' && (
                           <Button 
                             className="bg-primary hover:bg-primary/90"
                             onClick={() => toast({ title: 'Draft lobby coming soon' })}
                           >
                             <PlayCircle className="h-4 w-4 mr-2" />
                             Enter Draft Lobby
                           </Button>
                         )}
                         
                         {getDraftStatus().text === 'Live' && (
                           <Button 
                             className="bg-destructive hover:bg-destructive/90"
                             onClick={() => navigate(`/draft-room/${league?.id}`)}
                           >
                             <Activity className="h-4 w-4 mr-2" />
                             Enter Draft Room
                           </Button>
                         )}
                         
                         {getDraftStatus().text === 'Completed' && (
                           <Button variant="outline">
                             <Trophy className="h-4 w-4 mr-2" />
                             View Draft Results
                           </Button>
                         )}
                       </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Last Picks Strip (Live Only) */}
                {getDraftStatus().text === 'Live' && (
                  <Card className="mb-6">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Recent Picks</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex gap-3 overflow-x-auto pb-2">
                        {['Haaland', 'Salah', 'De Bruyne', 'Kane', 'Son', 'Benzema'].map((player, i) => (
                          <div key={i} className="flex-shrink-0 text-center">
                            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-1">
                              {player.slice(0, 2)}
                            </div>
                            <div className="text-xs text-muted-foreground">{player}</div>
                            <div className="text-xs text-muted-foreground">R{Math.floor(i/4) + 1}</div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Two Column Layout */}
                <div className="grid grid-cols-12 gap-6">
                  {/* Left Column - Main Content */}
                  <div className="col-span-8 space-y-6">
                    {/* Draft Order */}
                    <Card>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="flex items-center gap-2">
                            <Shuffle className="h-5 w-5" />
                            Draft Order
                          </CardTitle>
                          {isOwner && getDraftStatus().text === 'Upcoming' && (
                            <Dialog open={shuffleModalOpen} onOpenChange={setShuffleModalOpen}>
                              <DialogTrigger asChild>
                                <Button variant="outline" size="sm">
                                  <Shuffle className="h-4 w-4 mr-2" />
                                  Shuffle Order
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Shuffle Draft Order</DialogTitle>
                                  <DialogDescription>
                                    This will randomize the draft order for all teams. This action cannot be undone.
                                  </DialogDescription>
                                </DialogHeader>
                                <DialogFooter>
                                  <Button variant="outline" onClick={() => setShuffleModalOpen(false)}>
                                    Cancel
                                  </Button>
                                  <Button onClick={() => {
                                    // Shuffle logic here
                                    setShuffleModalOpen(false);
                                    toast({ title: 'Draft order shuffled!' });
                                  }}>
                                    Shuffle Order
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        {participants.length > 0 ? (
                          <div className="space-y-3">
                            {/* Round Headers */}
                            <div className="flex justify-between text-sm font-medium text-muted-foreground mb-4">
                              <span>Round 1 (1→{participants.length})</span>
                              <span>Round 2 ({participants.length}→1)</span>
                              <span>Round 3 (1→{participants.length})</span>
                            </div>
                            
                            {participants.map((participant, index) => (
                              <div 
                                key={participant.id}
                                className={`flex items-center justify-between p-3 rounded-lg border ${
                                  participant.user_id === user?.id ? 'bg-primary/5 border-primary/20' : 'bg-muted/30'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-background rounded-full flex items-center justify-center text-sm font-bold border">
                                    {index + 1}
                                  </div>
                                  <div>
                                    <div className="font-medium">{participant.profiles.display_name || participant.profiles.username}</div>
                                    <div className="text-sm text-muted-foreground">@{participant.profiles.username}</div>
                                  </div>
                                  {participant.user_id === user?.id && (
                                    <Badge variant="secondary">Your Team</Badge>
                                  )}
                                </div>
                                
                                {/* Snake Pattern Visualization */}
                                <div className="flex gap-2 text-sm font-mono">
                                  <span className="w-8 text-center">{index + 1}</span>
                                  <span className="w-8 text-center">{participants.length * 2 - index}</span>
                                  <span className="w-8 text-center">{participants.length * 2 + index + 1}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <Shuffle className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                            <h3 className="text-lg font-semibold mb-2">No Draft Order Generated</h3>
                            <p className="text-muted-foreground mb-4">Generate the draft order to begin preparation</p>
                            {isOwner && (
                              <Button>
                                <Shuffle className="h-4 w-4 mr-2" />
                                Generate Draft Order
                              </Button>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Participants & Readiness */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Users className="h-5 w-5" />
                          Participants & Readiness
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                          {participants.map((participant) => (
                            <div key={participant.id} className="flex items-center justify-between p-3 border rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="relative">
                                  <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                                    {(participant.profiles.display_name || participant.profiles.username).slice(0, 2).toUpperCase()}
                                  </div>
                                  {/* Online status dot */}
                                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-background rounded-full"></div>
                                </div>
                                <div>
                                  <div className="font-medium text-sm">{participant.profiles.display_name || participant.profiles.username}</div>
                                  <div className="text-xs text-muted-foreground">@{participant.profiles.username}</div>
                                </div>
                              </div>
                              
                               {participant.user_id === user?.id ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground">I'm Ready</span>
                                  <Switch
                                    checked={participantReadiness.find(p => p.user_id === user?.id)?.is_ready || false}
                                    onCheckedChange={toggleReadiness}
                                    disabled={isUpdatingReadiness}
                                  />
                                </div>
                               ) : (
                                 <Badge variant={participantReadiness.find(p => p.user_id === participant.user_id)?.is_ready ? "default" : "secondary"}>
                                   {participantReadiness.find(p => p.user_id === participant.user_id)?.is_ready ? (
                                     <>
                                       <CheckCircle className="h-3 w-3 mr-1" />
                                       Ready
                                     </>
                                   ) : (
                                     <>
                                       <XCircle className="h-3 w-3 mr-1" />
                                       Not Ready
                                     </>
                                   )}
                                 </Badge>
                               )}
                            </div>
                          ))}
                        </div>
                        
                        {isOwner && getDraftStatus().text === 'Upcoming' && (
                          <div className="mt-4 p-3 border rounded-lg bg-muted/30">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-medium">Auto-Pick on Timeout</span>
                              </div>
                              <Switch defaultChecked />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              Automatically pick the highest ranked available player when time expires
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Rules Summary */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Shield className="h-5 w-5" />
                          Draft Rules
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium">Draft Type:</span>
                            <span className="ml-2">Snake</span>
                          </div>
                          <div>
                            <span className="font-medium">Pick Clock:</span>
                            <span className="ml-2">60 seconds</span>
                          </div>
                          <div>
                            <span className="font-medium">Roster Size:</span>
                            <span className="ml-2">{league?.starters_count || 11} starters + {league?.bench_count || 10} bench</span>
                          </div>
                          <div>
                            <span className="font-medium">Auto-Pick:</span>
                            <span className="ml-2">Enabled on timeout</span>
                          </div>
                        </div>
                        <Button variant="link" className="mt-3 p-0 h-auto text-sm">
                          See full draft rules →
                        </Button>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Right Column - Side Panel */}
                  <div className="col-span-4 space-y-6">
                    {/* Pre-Draft Queue */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Star className="h-4 w-4" />
                          My Queue
                        </CardTitle>
                        <CardDescription className="text-sm">
                          Players will be auto-picked from your queue if time runs out
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Search */}
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                          <Input
                            placeholder="Search players..."
                            value={playerSearchTerm}
                            onChange={(e) => setPlayerSearchTerm(e.target.value)}
                            className="pl-9 text-sm"
                            size={32}
                          />
                        </div>
                        
                        {/* Filters */}
                        <div className="flex gap-2">
                          <Select value={playerPositionFilter} onValueChange={setPlayerPositionFilter}>
                            <SelectTrigger className="text-sm h-8">
                              <SelectValue placeholder="Position" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Positions</SelectItem>
                              <SelectItem value="GK">GK</SelectItem>
                              <SelectItem value="DEF">DEF</SelectItem>
                              <SelectItem value="MID">MID</SelectItem>
                              <SelectItem value="FWD">FWD</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Available Players */}
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {availablePlayers
                            .filter(player => 
                              playerPositionFilter === 'all' || player.position === playerPositionFilter
                            )
                            .filter(player =>
                              playerSearchTerm === '' || 
                              player.name.toLowerCase().includes(playerSearchTerm.toLowerCase())
                            )
                            .map((player) => (
                              <div key={player.id} className="flex items-center justify-between p-2 border rounded text-xs">
                                <div>
                                  <div className="font-medium">{player.name}</div>
                                  <div className="text-muted-foreground">{player.team} • {player.position}</div>
                                </div>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="h-6 px-2 text-xs"
                                  onClick={() => setMyQueue([...myQueue, player])}
                                >
                                  <Plus className="h-3 w-3" />
                                </Button>
                              </div>
                            ))
                          }
                        </div>
                        
                        {/* My Queue */}
                        <div className="space-y-2">
                          <div className="text-sm font-medium">My Queue ({myQueue.length})</div>
                          {myQueue.length === 0 ? (
                            <div className="text-xs text-muted-foreground text-center py-4 border rounded-lg border-dashed">
                              No players queued
                            </div>
                          ) : (
                            <div className="space-y-1">
                              {myQueue.map((player, index) => (
                                <div key={player.id} className="flex items-center justify-between p-2 bg-muted/50 rounded text-xs">
                                  <div className="flex items-center gap-2">
                                    <span className="w-4 h-4 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">
                                      {index + 1}
                                    </span>
                                    <div>
                                      <div className="font-medium">{player.name}</div>
                                      <div className="text-muted-foreground">{player.position}</div>
                                    </div>
                                  </div>
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    className="h-6 w-6 p-0"
                                    onClick={() => setMyQueue(myQueue.filter(p => p.id !== player.id))}
                                  >
                                    <XCircle className="h-3 w-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* System Check */}
                    {getDraftStatus().text === 'Upcoming' && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Wifi className="h-4 w-4" />
                            System Check
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span className="text-sm">Connection</span>
                            </div>
                            <span className="text-xs text-green-600">OK</span>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span className="text-sm">Notifications</span>
                            </div>
                            <span className="text-xs text-green-600">OK</span>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {systemCheck.audio ? (
                                <Volume2 className="h-4 w-4 text-green-500" />
                              ) : (
                                <VolumeX className="h-4 w-4 text-muted-foreground" />
                              )}
                              <span className="text-sm">Audio Cues</span>
                            </div>
                            <Switch 
                              checked={systemCheck.audio}
                              onCheckedChange={(checked) => 
                                setSystemCheck(prev => ({ ...prev, audio: checked }))
                              }
                            />
                          </div>
                          
                          <Button variant="outline" size="sm" className="w-full">
                            <Volume2 className="h-4 w-4 mr-2" />
                            Test Sound
                          </Button>
                        </CardContent>
                      </Card>
                    )}

                    {/* Activity Feed */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Activity className="h-4 w-4" />
                          Activity
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3 max-h-48 overflow-y-auto">
                          {mockActivity.map((activity) => (
                            <div key={activity.id} className="flex gap-3">
                              <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                              <div className="flex-1">
                                <p className="text-sm">{activity.message}</p>
                                <p className="text-xs text-muted-foreground">
                                  {formatTimeAgo(activity.created_at)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Owner Controls */}
                    {isOwner && getDraftStatus().text === 'Upcoming' && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Settings className="h-4 w-4" />
                            Draft Controls
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <Button variant="outline" size="sm" className="w-full justify-start">
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Send Reminder to All
                          </Button>
                          <Button variant="outline" size="sm" className="w-full justify-start">
                            <Lock className="h-4 w-4 mr-2" />
                            Lock Draft Settings
                          </Button>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="players" className="mt-0">
                <div className="space-y-6">
                  <Card>
                    <CardHeader>  
                      <CardTitle className="flex items-center gap-2">
                        <Trophy className="h-5 w-5" />
                        Player Rankings & Statistics
                      </CardTitle>
                      <CardDescription>
                        Complete standings and player performance metrics
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {participants.length === 0 ? (
                        <div className="text-center py-12">
                          <Users className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
                          <h3 className="text-xl font-semibold mb-2">No players yet</h3>
                          <p className="text-muted-foreground mb-4">
                            Invite players to join your league
                          </p>
                          {isOwner && (
                            <Button onClick={() => setInviteModalOpen(true)}>
                              <Share className="h-4 w-4 mr-2" />
                              Invite Players
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-6">
                          {/* Detailed Player Statistics Table */}
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-16">Rank</TableHead>
                                <TableHead>Player</TableHead>
                                <TableHead className="text-center">Games</TableHead>
                                <TableHead className="text-center">W-L-T</TableHead>
                                <TableHead className="text-center">Win %</TableHead>
                                <TableHead className="text-right">Points For</TableHead>
                                <TableHead className="text-right">Points Against</TableHead>
                                <TableHead className="text-right">Diff</TableHead>
                                <TableHead className="text-center">Streak</TableHead>
                                <TableHead className="text-center">Last Week</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {participants.map((participant, index) => {
                                const wins = 0;
                                const losses = 0;
                                const ties = 0;
                                const pointsFor = Math.floor(Math.random() * 1000) + 500;
                                const pointsAgainst = Math.floor(Math.random() * 1000) + 500;
                                const diff = pointsFor - pointsAgainst;
                                const winPercentage = wins + losses + ties > 0 ? (wins / (wins + losses + ties) * 100).toFixed(1) : '0.0';
                                const lastWeekPoints = Math.floor(Math.random() * 150) + 50;
                                
                                return (
                                  <TableRow 
                                    key={participant.id}
                                    className={participant.user_id === user?.id ? "bg-primary/5 border-primary/20" : ""}
                                  >
                                    <TableCell className="font-medium">
                                      <div className="flex items-center gap-2">
                                        {index + 1}
                                        {index === 0 && <Trophy className="h-4 w-4 text-yellow-500" />}
                                        {index === 1 && <Trophy className="h-4 w-4 text-gray-400" />}
                                        {index === 2 && <Trophy className="h-4 w-4 text-amber-600" />}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <div className="space-y-1">
                                        <div className="font-medium">
                                          {participant.profiles?.display_name || participant.profiles?.username}
                                          {participant.user_id === user?.id && (
                                            <Badge variant="secondary" className="ml-2">You</Badge>
                                          )}
                                        </div>
                                        <div className="text-sm text-muted-foreground">
                                          @{participant.profiles?.username}
                                        </div>
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-center font-mono">
                                      {wins + losses + ties}
                                    </TableCell>
                                    <TableCell className="text-center font-mono">
                                      {wins}-{losses}-{ties}
                                    </TableCell>
                                    <TableCell className="text-center font-mono">
                                      {winPercentage}%
                                    </TableCell>
                                    <TableCell className="text-right font-mono">
                                      {pointsFor.toLocaleString()}
                                    </TableCell>
                                    <TableCell className="text-right font-mono">
                                      {pointsAgainst.toLocaleString()}
                                    </TableCell>
                                    <TableCell className={`text-right font-mono ${diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {diff >= 0 ? '+' : ''}{diff.toLocaleString()}
                                    </TableCell>
                                    <TableCell className="text-center">
                                      <Badge variant="outline" className="text-xs">
                                        -
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-center font-mono">
                                      {lastWeekPoints}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>

                          {/* Additional Stats Cards */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                            <Card>
                              <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-medium">League Leader</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-1">
                                  <div className="font-semibold">
                                    {participants[0]?.profiles?.display_name || participants[0]?.profiles?.username || 'No players'}
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    {participants.length > 0 ? '0 points' : 'Season hasn\'t started'}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>

                            <Card>
                              <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-medium">Most Points This Week</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-1">
                                  <div className="font-semibold">
                                    {participants[0]?.profiles?.display_name || participants[0]?.profiles?.username || 'No players'}
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    {participants.length > 0 ? Math.floor(Math.random() * 150) + 100 + ' points' : 'No scores yet'}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>

                            <Card>
                              <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-medium">League Average</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-1">
                                  <div className="font-semibold">
                                    {participants.length > 0 ? Math.floor(Math.random() * 100) + 50 : '0'} pts/week
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    Across {participants.length} players
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="matchups" className="mt-0">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center py-12">
                      <Trophy className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Matchups tab content coming soon</h3>
                      <p className="text-muted-foreground">Weekly matchups and results will be available here.</p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="settings" className="mt-0">
                <LeagueSettings 
                  league={league}
                  isOwner={isOwner}
                  participantCount={participants.length}
                  onLeagueUpdate={(updates) => {
                    setLeague(prev => prev ? { ...prev, ...updates } : prev);
                    // Refresh league data after update
                    fetchLeagueDetails();
                  }}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default LeagueOverview;